import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import pageCatalog from '@/i18n/ns/connections-page';
import { isConnecting } from './connection-api';
import {
  CatalogueCard,
  connectedTileAction,
  tileStateFor,
  type CatalogueConnection,
  type CatalogueTileState,
} from './catalogue-card';
import {
  itemFor,
  type PipedreamApp,
  type ScopedConnectionPresentation,
} from './pipedream-catalogue-api';

export function PipedreamAppCard({
  app,
  busy,
  configured,
  canConnect,
  companyName,
  connection,
  manage,
  changing,
  onConnect,
  onToggle,
}: {
  app: PipedreamApp;
  busy: string | null;
  configured: boolean;
  canConnect: boolean;
  companyName: string;
  /** This person's account for the app, when they have one. */
  connection: CatalogueConnection | null;
  /** An admin is choosing which apps members may connect: the tile shows the switch. */
  manage: boolean;
  changing: boolean;
  onConnect: (item: ScopedConnectionPresentation) => void;
  onToggle: () => void;
}) {
  const copy = useNamespaceCopy(pageCatalog);
  const allowed = app.enabled !== false;
  const target = itemFor(app, 'private');
  const connecting = isConnecting(busy, target);
  const state: CatalogueTileState = connecting
    ? 'busy'
    : connection
      ? tileStateFor(connection.state)
      : !allowed
        ? 'not_allowed'
        : !configured || !canConnect
          ? 'unavailable'
          : 'connect';
  const action =
    state === 'busy'
      ? { label: copy.tileConnecting, onClick: () => undefined }
      : connection
        ? connectedTileAction(state, connection, copy, Boolean(busy))
        : {
            label:
              state === 'not_allowed'
                ? copy.tileNotAllowed
                : state === 'unavailable'
                  ? copy.tileUnavailable
                  : copy.tileConnect,
            disabled: state !== 'connect' || Boolean(busy),
            onClick: () => onConnect(target),
          };
  const subtitle = connection
    ? (connection.identity ?? copy.tileConnected)
    : !allowed
      ? copy.tileNotAllowedNote
      : app.hasActions === false
        ? copy.tileAccountOnly
        : app.authType === 'keys'
          ? copy.tileApiKey
          : app.categories?.[0];
  return (
    <CatalogueCard
      source="pipedream"
      logo={app.img_src}
      name={app.name}
      subtitle={subtitle}
      description={app.description}
      state={state}
      action={action}
      manage={
        manage ? (
          <button
            type="button"
            role="switch"
            className="ws-pipedream-switch"
            aria-checked={allowed}
            aria-label={formatCopy(copy.allowFor, { name: app.name, company: companyName })}
            disabled={changing || Boolean(busy)}
            onClick={onToggle}
          >
            <span />
          </button>
        ) : undefined
      }
    />
  );
}
